import { z } from 'zod'

import {
  civilDateSchema,
  planMasterRowValidFromRefine,
  validPeriodRefine,
  validPeriodRefineIssue,
  withinRunes,
} from '~/pages/AdminOcctoMaster/tabs/_common/schema'

/**
 * 抑制契約 (contracts[] の 1 エントリ) input schema。
 * proto SuppressionContractInput が 4 field を required で要求するため
 * 4 つ全て必須入力 (SuppressionContractsSection.tsx 冒頭 JSDoc 参照)。
 * contractId の 20 文字上限は spec demand-suppression-plan.md
 * 契約識別番号 (proto CEL SuppressionContractInput.contract_id max_len
 * 20 と層間一致、4722-master-input-char-limits 設計書)。
 */
// spec demand-suppression-plan.md AC-OMD-002 [P0] verbatim。 zod v4 の
// `.min(1)` に message を渡さないと SuppressionContractsSection 側で
// FieldErrorText として zod 既定の英文が UI に露出するため 4 field 全てに
// spec 文言を明示する。
export const suppressionContractInputSchema = z.object({
  suppressionBgMasterId: z.string().min(1, '需要抑制 BG を選択してください'),
  partnerBgMasterId: z.string().min(1, '取引先 BG を選択してください'),
  businessMasterId: z.string().min(1, '契約締結事業者を選択してください'),
  contractId: z
    .string()
    .min(1, '契約識別番号を入力してください')
    .refine(withinRunes(20), '契約識別番号は20文字以内で入力してください'),
  validFrom: civilDateSchema,
  // クリア時は RHF field-array の制約で `null` が入る (PeriodFields の
  // clearAsNull JSDoc 参照)。`null` を許容し `undefined` へ正規化する。
  validTo: civilDateSchema
    .nullable()
    .transform((v) => v ?? undefined)
    .optional(),
})

/**
 * 取引先 BG (調達側 / 販売側) の 1 行。
 * AC-OMD-003: BG 未選択は「BG を選択してください」。message を省くと
 * zod 既定の英文が FieldErrorText に出る。
 */
export const suppressionPartnerBgInputSchema = z.object({
  side: z.number().int(),
  bgMasterId: z.string().min(1, 'BG を選択してください'),
  validFrom: civilDateSchema,
  // クリア時は RHF field-array の制約で `null` が入る (PeriodFields の
  // clearAsNull JSDoc 参照)。`null` を許容し `undefined` へ正規化する。
  validTo: civilDateSchema
    .nullable()
    .transform((v) => v ?? undefined)
    .optional(),
})

/**
 * 需要抑制計画マスタ Create / Edit 共通 Zod schema。
 *
 * Create では submitterMasterId / submitterCode (new) を指定する。
 * Update では id のみで識別し submitterCode / submitterMasterId は変更不可。
 * contracts[] / partnerBgs[] は full replacement (PUT セマンティクス)。
 * contracts / partnerBgs の詳細入力フォームは将来 PR で実装予定。
 */
export const demandSuppressionMasterFormSchema = z
  .object({
    /** 計画提出者マスタ UUID (Create のみ使用) */
    submitterMasterId: z.string().min(1, '計画提出者は必須です'),
    /** OCCTO 5 桁計画提出者コード (Read-only / Create 時スナップショット) */
    submitterCode: z.string().min(1, '計画提出者コードは必須です'),
    /** 備考 (任意) */
    note: z
      .string()
      .refine(withinRunes(100), '備考は100文字以内で入力してください')
      .optional(),
    /** 抑制契約リスト (将来の詳細入力フォームで使用) */
    contracts: z.array(suppressionContractInputSchema),
    /** 調達販売 BG リスト */
    partnerBgs: z.array(suppressionPartnerBgInputSchema),
    /** 適用開始日 (必須) */
    validFrom: civilDateSchema,
    /** 適用終了日 (任意) */
    validTo: civilDateSchema.optional(),
  })
  .refine(validPeriodRefine, validPeriodRefineIssue)
  .superRefine((value, ctx) => {
    planMasterRowValidFromRefine(
      value.validFrom,
      value.contracts,
      '需要抑制契約の適用開始日はレコードの適用開始日以降の日付で入力してください',
      'contracts',
      ctx,
    )
    planMasterRowValidFromRefine(
      value.validFrom,
      value.partnerBgs,
      '取引先 BG の適用開始日はレコードの適用開始日以降の日付で入力してください',
      'partnerBgs',
      ctx,
    )
  })

export type DemandSuppressionMasterFormValues = z.infer<
  typeof demandSuppressionMasterFormSchema
>
